import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Save, RefreshCw, Tag, FlaskConical, Rocket } from 'lucide-react';

/**
 * LMS Premium pricing editor — content-store driven (no redeploy needed).
 * Holds BOTH sandbox and live PayPal credentials + an `environment` switch.
 * Reads the raw config from GET /api/admin/lms/pricing and writes it back via
 * PUT. The public paywall consumes only the resolved active set from
 * GET /api/lms/pricing, so switching environment here is instant.
 */
export const AdminPricingTab = ({ backendUrl, token, showToast }) => {
  const [tiers, setTiers] = useState([]);
  const [environment, setEnvironment] = useState('sandbox');
  const [clientIdLive, setClientIdLive] = useState('');
  const [clientIdSandbox, setClientIdSandbox] = useState('');
  const [paywallEnabled, setPaywallEnabled] = useState(true);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchPricing = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${backendUrl}/api/admin/lms/pricing`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTiers(res.data.tiers || []);
      setEnvironment(res.data.environment || 'sandbox');
      setClientIdLive(res.data.client_id_live || '');
      setClientIdSandbox(res.data.client_id_sandbox || '');
      setPaywallEnabled(res.data.paywall_enabled !== false);
    } catch (e) {
      showToast?.('Errore nel caricamento dei prezzi', 'error');
    } finally {
      setLoading(false);
    }
  }, [backendUrl, token, showToast]);

  const fetchSubs = useCallback(async () => {
    try {
      const res = await axios.get(`${backendUrl}/api/admin/lms/subscriptions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubs(res.data.subscriptions || []);
    } catch (e) { /* non-blocking */ }
  }, [backendUrl, token]);

  useEffect(() => { fetchPricing(); fetchSubs(); }, [fetchPricing, fetchSubs]);

  const updateField = (idx, field, value) => {
    setTiers((prev) => prev.map((t, i) => (i === idx ? { ...t, [field]: value } : t)));
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${backendUrl}/api/admin/lms/pricing`,
        {
          tiers,
          environment,
          client_id_live: clientIdLive,
          client_id_sandbox: clientIdSandbox,
          paywall_enabled: paywallEnabled,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      showToast?.(`Salvato — paywall in modalità ${environment.toUpperCase()}`, 'success');
    } catch (e) {
      showToast?.(e?.response?.data?.detail || 'Salvataggio fallito', 'error');
    } finally {
      setSaving(false);
    }
  };

  const FIELDS = [
    { key: 'label', label: 'Etichetta', ph: 'Mensile' },
    { key: 'price', label: 'Prezzo', ph: '€19' },
    { key: 'period', label: 'Periodo', ph: '/ mese' },
    { key: 'sub', label: 'Sottotitolo', ph: 'Rinnovo automatico…' },
    { key: 'cta', label: 'Testo bottone', ph: 'Inizia' },
    { key: 'badge', label: 'Badge (opzionale)', ph: 'Più scelto' },
  ];

  const isSandbox = environment === 'sandbox';

  return (
    <div className="space-y-6" data-testid="admin-pricing-tab">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 text-white">
          <Tag className="w-5 h-5 text-orange-400" />
          <h2 className="text-lg font-bold">Prezzi Premium (Paywall)</h2>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchPricing} disabled={loading} className="bg-slate-700 hover:bg-slate-600" data-testid="admin-pricing-reload">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Ricarica
          </Button>
          <Button onClick={save} disabled={saving || !tiers.length} className="bg-emerald-600 hover:bg-emerald-500" data-testid="admin-pricing-save">
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Salvo…' : 'Salva'}
          </Button>
        </div>
      </div>

      <p className="text-sm text-slate-400">
        Modifica i piani mostrati nel paywall. Le modifiche sono immediate, senza rideploy.
      </p>

      {/* PayPal environment switch — sandbox (test) vs live (reale) */}
      <div className="rounded-xl border border-slate-700 bg-slate-800/50 p-4 space-y-3" data-testid="admin-pricing-env">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-white font-bold text-sm">Ambiente PayPal</p>
            <p className="text-xs text-slate-400">Sandbox = test senza addebiti reali · Live = pagamenti veri. Passa a Live solo prima del deploy.</p>
          </div>
          <div className="flex rounded-lg overflow-hidden border border-slate-600" role="group">
            <button
              type="button"
              onClick={() => setEnvironment('sandbox')}
              data-testid="admin-pricing-env-sandbox"
              className={`px-3 py-2 text-xs font-bold flex items-center gap-1.5 transition-colors ${isSandbox ? 'bg-amber-500 text-slate-900' : 'bg-slate-900/60 text-slate-400 hover:text-white'}`}
            >
              <FlaskConical className="w-3.5 h-3.5" /> Sandbox
            </button>
            <button
              type="button"
              onClick={() => setEnvironment('live')}
              data-testid="admin-pricing-env-live"
              className={`px-3 py-2 text-xs font-bold flex items-center gap-1.5 transition-colors ${!isSandbox ? 'bg-emerald-500 text-slate-900' : 'bg-slate-900/60 text-slate-400 hover:text-white'}`}
            >
              <Rocket className="w-3.5 h-3.5" /> Live
            </button>
          </div>
        </div>
        <div className="text-[11px] font-bold uppercase tracking-wider">
          Attivo:{' '}
          <span className={isSandbox ? 'text-amber-300' : 'text-emerald-300'} data-testid="admin-pricing-env-active">
            {environment}
          </span>
        </div>
      </div>

      {/* PayPal client-ids (public) — one per environment */}
      <div className="grid md:grid-cols-2 gap-4">
        <label className={`block rounded-xl border p-4 ${isSandbox ? 'border-amber-500/50 bg-amber-500/5' : 'border-slate-700 bg-slate-800/50'}`}>
          <span className="text-[11px] uppercase tracking-wider text-amber-300 font-semibold flex items-center gap-1.5"><FlaskConical className="w-3.5 h-3.5" /> Client ID Sandbox</span>
          <input
            type="text"
            value={clientIdSandbox}
            placeholder="Client ID app sandbox"
            onChange={(e) => setClientIdSandbox(e.target.value)}
            className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-600 bg-slate-900/60 text-white placeholder:text-slate-500 focus:border-amber-400 outline-none font-mono text-xs"
            data-testid="admin-pricing-client-id-sandbox"
          />
        </label>
        <label className={`block rounded-xl border p-4 ${!isSandbox ? 'border-emerald-500/50 bg-emerald-500/5' : 'border-slate-700 bg-slate-800/50'}`}>
          <span className="text-[11px] uppercase tracking-wider text-emerald-300 font-semibold flex items-center gap-1.5"><Rocket className="w-3.5 h-3.5" /> Client ID Live</span>
          <input
            type="text"
            value={clientIdLive}
            placeholder="Client ID app live"
            onChange={(e) => setClientIdLive(e.target.value)}
            className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-600 bg-slate-900/60 text-white placeholder:text-slate-500 focus:border-emerald-400 outline-none font-mono text-xs"
            data-testid="admin-pricing-client-id-live"
          />
        </label>
      </div>

      {/* Publish / hide paywall kill switch */}
      <div className="flex items-center justify-between gap-4 rounded-xl border border-slate-700 bg-slate-800/50 p-4" data-testid="admin-paywall-toggle-row">
        <div>
          <p className="text-white font-bold text-sm">Paywall attivo</p>
          <p className="text-xs text-slate-400">Se disattivato, il paywall mostra “iscrizioni chiuse” e nasconde il pagamento.</p>
        </div>
        <button
          type="button"
          onClick={() => setPaywallEnabled((v) => !v)}
          data-testid="admin-paywall-toggle"
          className={`relative w-14 h-7 rounded-full transition-colors ${paywallEnabled ? 'bg-emerald-500' : 'bg-slate-600'}`}
          aria-pressed={paywallEnabled}
        >
          <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white transition-transform ${paywallEnabled ? 'translate-x-7' : ''}`} />
        </button>
      </div>

      {/* Tiers editor — includes a Plan ID field per environment */}
      <div className="grid md:grid-cols-2 gap-4">
        {tiers.map((t, idx) => (
          <div key={t.id || idx} className="rounded-xl border border-slate-700 bg-slate-800/50 p-5 space-y-3" data-testid={`admin-pricing-tier-${t.id || idx}`}>
            <p className="text-xs uppercase tracking-widest text-orange-300 font-bold">{t.id}</p>
            {FIELDS.map((f) => (
              <label key={f.key} className="block">
                <span className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{f.label}</span>
                <input
                  type="text"
                  value={t[f.key] ?? ''}
                  placeholder={f.ph}
                  onChange={(e) => updateField(idx, f.key, e.target.value)}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-600 bg-slate-900/60 text-white placeholder:text-slate-500 focus:border-orange-400 outline-none"
                  data-testid={`admin-pricing-${t.id || idx}-${f.key}`}
                />
              </label>
            ))}
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-amber-300 font-semibold flex items-center gap-1.5"><FlaskConical className="w-3.5 h-3.5" /> Plan ID Sandbox</span>
              <input
                type="text"
                value={t.plan_id_sandbox ?? ''}
                placeholder="P-XXXX (sandbox)"
                onChange={(e) => updateField(idx, 'plan_id_sandbox', e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-600 bg-slate-900/60 text-white placeholder:text-slate-500 focus:border-amber-400 outline-none font-mono text-xs"
                data-testid={`admin-pricing-${t.id || idx}-plan_id_sandbox`}
              />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-emerald-300 font-semibold flex items-center gap-1.5"><Rocket className="w-3.5 h-3.5" /> Plan ID Live</span>
              <input
                type="text"
                value={t.plan_id_live ?? ''}
                placeholder="P-XXXX (live)"
                onChange={(e) => updateField(idx, 'plan_id_live', e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-600 bg-slate-900/60 text-white placeholder:text-slate-500 focus:border-emerald-400 outline-none font-mono text-xs"
                data-testid={`admin-pricing-${t.id || idx}-plan_id_live`}
              />
            </label>
            <label className="flex items-center gap-2 pt-1">
              <input
                type="checkbox"
                checked={!!t.highlight}
                onChange={(e) => updateField(idx, 'highlight', e.target.checked)}
                data-testid={`admin-pricing-${t.id || idx}-highlight`}
              />
              <span className="text-sm text-slate-300">Piano in evidenza</span>
            </label>
          </div>
        ))}
      </div>

      {/* Received subscriptions (read-only) */}
      <div className="rounded-xl border border-slate-700 bg-slate-800/50 p-5" data-testid="admin-pricing-subscriptions">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white font-bold text-sm">Abbonamenti ricevuti ({subs.length})</h3>
          <Button onClick={fetchSubs} className="bg-slate-700 hover:bg-slate-600 h-8 px-3 text-xs" data-testid="admin-subs-reload">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Aggiorna
          </Button>
        </div>
        {subs.length === 0 ? (
          <p className="text-xs text-slate-500">Nessun abbonamento ancora ricevuto.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-slate-400 text-left uppercase tracking-wider">
                  <th className="py-1.5 pr-3">Email</th>
                  <th className="py-1.5 pr-3">Piano</th>
                  <th className="py-1.5 pr-3">Subscription ID</th>
                  <th className="py-1.5 pr-3">Stato</th>
                  <th className="py-1.5">Data</th>
                </tr>
              </thead>
              <tbody>
                {subs.map((s) => (
                  <tr key={s.subscription_id} className="border-t border-slate-700/60 text-slate-200">
                    <td className="py-1.5 pr-3">{s.email || '—'}</td>
                    <td className="py-1.5 pr-3">{s.tier_id || s.plan_id}</td>
                    <td className="py-1.5 pr-3 font-mono">{s.subscription_id}</td>
                    <td className="py-1.5 pr-3"><span className="px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300">{s.status}</span></td>
                    <td className="py-1.5">{(s.created_at || '').slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPricingTab;
